// Marketplace filters — URL state and which facet groups are worth showing.
// Pure functions (tested in marketFilters.test.js); the page renders them.
//
// URL: compact, stable keys only — q, sort, min, max, moq, rating, tiers,
// variants, f (attributes: "material.plastic,color.black"). Never supplier
// text. The retired ?specs= form (raw Chinese name~value) is dropped on read,
// so an old bookmark opens the same category, unfiltered, instead of failing.

import { attributeInScope, attributeValueLabel, attributeValues, decodeAttrFilter, encodeAttrFilter, MARKET_ATTRIBUTE_BY_KEY, MARKET_ATTRIBUTES, scopeGroups } from './marketAttributes';

export const SORTS = ['relevance', 'price_asc', 'price_desc', 'rating', 'newest'];
const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/** URLSearchParams → filter state. */
export function readFilters(params) {
  const g = (k) => params.get(k);
  const sort = SORTS.includes(g('sort')) ? g('sort') : 'relevance';
  const priceMin = num(g('min')), priceMax = num(g('max'));
  return {
    q: (g('q') || '').slice(0, 200),
    sort,
    priceMin: priceMin != null && priceMin >= 0 ? priceMin : null,
    priceMax: priceMax != null && priceMax > 0 ? priceMax : null,
    moqMax: num(g('moq')) != null && num(g('moq')) > 0 ? Math.floor(num(g('moq'))) : null,
    minRating: num(g('rating')) != null && num(g('rating')) > 0 && num(g('rating')) <= 5 ? num(g('rating')) : null,
    hasTiers: g('tiers') === '1' ? true : null,
    hasVariants: g('variants') === '1' ? true : null,
    attrs: decodeAttrFilter(g('f')),
  };
}

/** Filter state → URLSearchParams (only what is set; stable order). */
export function writeFilters(f) {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  if (f.sort && f.sort !== 'relevance') p.set('sort', f.sort);
  if (f.priceMin != null) p.set('min', String(f.priceMin));
  if (f.priceMax != null) p.set('max', String(f.priceMax));
  if (f.moqMax != null) p.set('moq', String(f.moqMax));
  if (f.minRating != null) p.set('rating', String(f.minRating));
  if (f.hasTiers) p.set('tiers', '1');
  if (f.hasVariants) p.set('variants', '1');
  const enc = encodeAttrFilter(f.attrs);
  if (enc) p.set('f', enc);
  return p;
}

/** Did the URL carry something this page no longer understands (e.g. the old ?specs=)? */
export const hasRetiredParams = (params) => params.has('specs') || (params.get('f') && encodeAttrFilter(decodeAttrFilter(params.get('f'))) !== params.get('f'));

export function toggleAttr(f, attr, value) {
  const cur = f.attrs[attr] || [];
  const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
  const attrs = { ...f.attrs };
  if (next.length) attrs[attr] = next; else delete attrs[attr];
  return { ...f, attrs };
}

export const activeCount = (f) => Object.values(f.attrs).reduce((n, v) => n + v.length, 0)
  + (f.priceMin != null || f.priceMax != null ? 1 : 0) + (f.moqMax != null ? 1 : 0) + (f.minRating != null ? 1 : 0) + (f.hasTiers ? 1 : 0) + (f.hasVariants ? 1 : 0);

export const clearFilters = (f) => ({ ...f, priceMin: null, priceMax: null, moqMax: null, minRating: null, hasTiers: null, hasVariants: null, attrs: {} });

// The order shoppers of each scope group look for things (most decisive first);
// attributes not listed follow in registry order.
const ORDER = {
  electronics: ['feature', 'connectivity', 'compatibility', 'water_resistance', 'charging', 'charging_power', 'bluetooth_version', 'screen_type', 'plug', 'color', 'material', 'gender'],
  apparel: ['size', 'color', 'material', 'gender', 'audience', 'age_group', 'season', 'thickness', 'sleeve', 'pattern', 'style', 'feature'],
  furniture: ['material', 'filling', 'color', 'feature', 'style', 'shape', 'pattern', 'season'],
  packaging: ['packaging_type', 'material', 'color', 'shape', 'capacity', 'style'],
  home_appliances: ['plug', 'voltage', 'power', 'capacity', 'feature', 'material', 'color'],
  home_living: ['material', 'color', 'capacity', 'feature', 'style', 'pattern', 'shape'],
  toys: ['age_group', 'toy_type', 'material', 'gender', 'color', 'feature'],
};
const MAX_VALUES = 40;

/**
 * Facet rows → groups to render: [{ attr, label, values: [{ value, label, count, selected }] }].
 * Rules (each one fixes a failure seen on the batch):
 *   • only attributes in scope for the category (no "plug type" on clothing);
 *   • only values the registry can label — never raw supplier text, never Chinese;
 *   • a selected value always stays visible, even at count 0, so it can be removed;
 *   • a group that cannot narrow the results (one value covering everything,
 *     nothing selected) is not shown;
 *   • bucketed numbers keep their natural order; other values sort by count.
 */
export function facetGroups(rows, { category = null, total = 0, selected = {}, lang = 'ar' } = {}) {
  const by = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!attributeInScope(r.attr, category)) continue;
    const label = attributeValueLabel(r.attr, r.value, lang);
    if (!label || !(r.count > 0)) continue;
    if (!by.has(r.attr)) by.set(r.attr, new Map());
    by.get(r.attr).set(r.value, { value: r.value, label, count: r.count });
  }
  for (const [attr, vals] of Object.entries(selected || {})) {
    if (!MARKET_ATTRIBUTE_BY_KEY[attr]) continue;
    for (const v of vals) {
      const label = attributeValueLabel(attr, v, lang);
      if (!label) continue;
      if (!by.has(attr)) by.set(attr, new Map());
      if (!by.get(attr).has(v)) by.get(attr).set(v, { value: v, label, count: 0 });
    }
  }
  const order = [...scopeGroups(category).flatMap((g) => ORDER[g] || []), ...MARKET_ATTRIBUTES.map((a) => a.key)];
  const groups = [];
  for (const attr of [...new Set(order)]) {
    if (!by.has(attr)) continue;
    const a = MARKET_ATTRIBUTE_BY_KEY[attr];
    const sel = new Set(selected?.[attr] || []);
    let values = [...by.get(attr).values()].map((v) => ({ ...v, selected: sel.has(v.value) }));
    const natural = attributeValues(a).map((x) => x.value);
    if (a.parse && natural.length) values.sort((x, y) => natural.indexOf(x.value) - natural.indexOf(y.value));
    else values.sort((x, y) => (y.selected - x.selected) || (y.count - x.count) || x.label.localeCompare(y.label, lang));
    values = values.slice(0, MAX_VALUES);
    const useful = sel.size > 0 || values.length >= 2 || values.some((v) => v.count < total);
    if (!useful) continue;
    groups.push({ attr, label: lang === 'ar' ? a.ar : a.en, match: a.match === 'all' ? 'all' : 'any', values });
  }
  return groups;
}
